/**
 * Visual element selector mode (Rule Wizard).
 *
 * Activated via ?tst_select=<sessionId> — lets users pick clickable elements
 * on their own site. Self-contained: when active, normal tracking is skipped.
 */

import { state } from "./state.js";
import { BRAND_COLOR, MAX_SELECTOR_DEPTH, MAX_ELEMENT_TEXT_LENGTH, STORAGE_KEY_SELECTOR } from "./constants.js";

/**
 * Try to build a unique selector using the element's id.
 * Returns the selector if it uniquely identifies the element, otherwise null.
 */
function trySelectorById(el: Element): string | null {
  if (!el.id) return null;
  const sel = "#" + CSS.escape(el.id);
  if (document.querySelectorAll(sel).length === 1) return sel;
  return null;
}

/**
 * Try to build a unique selector using one of the element's classes.
 * Returns the selector if a single class uniquely identifies the element, otherwise null.
 */
function trySelectorByUniqueClass(el: Element): string | null {
  for (const className of el.classList) {
    const sel = "." + CSS.escape(className);
    if (document.querySelectorAll(sel).length === 1) return sel;
  }
  return null;
}

/**
 * Build a CSS selector by walking up the DOM tree (max 4 levels).
 *
 * At each level, tries (in order):
 * 1. Anchor on parent's id (stops walking)
 * 2. Use a class that is unique among siblings of the same tag
 * 3. Fall back to :nth-of-type(n)
 */
function buildPathSelector(el: Element): string {
  const parts: string[] = [];
  let current: Element | null = el;

  for (let depth = 0; depth < MAX_SELECTOR_DEPTH && current && current !== document.body; depth++) {
    const tag = current.tagName.toLowerCase();

    // Anchor on id — no need to walk further up
    if (current.id) {
      parts.unshift("#" + CSS.escape(current.id));
      break;
    }

    // Try to find a class that uniquely identifies this element among its siblings
    let part = tag;
    const uniqueClass = findUniqueSiblingClass(current, tag);

    if (uniqueClass) {
      part = tag + "." + CSS.escape(uniqueClass);
    } else {
      // nth-of-type fallback for disambiguation
      const siblings = current.parentElement
        ? Array.from(current.parentElement.children).filter(
            (c) => c.tagName === current!.tagName,
          )
        : [];
      if (siblings.length > 1) {
        const idx = siblings.indexOf(current) + 1;
        part = tag + ":nth-of-type(" + idx + ")";
      }
    }

    parts.unshift(part);
    current = current.parentElement;
  }

  return parts.join(" > ");
}

/** Find a class on `el` that is unique among same-tag siblings in its parent. */
function findUniqueSiblingClass(el: Element, tag: string): string | null {
  if (!el.parentElement) return null;

  for (const className of el.classList) {
    const testSel = tag + "." + CSS.escape(className);
    if (el.parentElement.querySelectorAll(":scope > " + testSel).length === 1) {
      return className;
    }
  }
  return null;
}

/**
 * Try to build a unique selector using a data attribute or aria-label.
 * Checks common CMS/builder data attributes (Divi, Elementor, etc.) first.
 */
function trySelectorByDataAttr(el: Element): string | null {
  const dataAttrs = ["data-module-id", "data-id", "data-block-id", "data-element-id"];
  for (const attr of dataAttrs) {
    const val = el.getAttribute(attr);
    if (!val) continue;
    const sel = `[${attr}="${CSS.escape(val)}"]`;
    if (document.querySelectorAll(sel).length === 1) return sel;
  }
  const ariaLabel = el.getAttribute("aria-label");
  if (ariaLabel) {
    const sel = `[aria-label="${CSS.escape(ariaLabel)}"]`;
    if (document.querySelectorAll(sel).length === 1) return sel;
  }
  return null;
}

/**
 * Try to build a unique selector using a combination of two classes.
 * Useful for CSS frameworks that use multiple utility classes (Divi, Tailwind, etc.).
 */
function trySelectorByClassCombo(el: Element): string | null {
  const tag = el.tagName.toLowerCase();
  const classes = Array.from(el.classList);
  if (classes.length < 2) return null;
  for (let i = 0; i < classes.length; i++) {
    for (let j = i + 1; j < classes.length; j++) {
      const sel = `${tag}.${CSS.escape(classes[i])}.${CSS.escape(classes[j])}`;
      if (document.querySelectorAll(sel).length === 1) return sel;
    }
  }
  return null;
}

/**
 * Generate a CSS selector that uniquely identifies an element.
 *
 * Strategy (returns first match):
 * 1. Try #id if the element has a unique id
 * 2. Try [data-*] or [aria-label] attribute
 * 3. Try .className if a single class uniquely identifies it
 * 4. Try tag.classA.classB multi-class combo
 * 5. Build a parent path selector (up to 4 levels deep)
 */
function generateSelector(el: Element): string {
  const byId = trySelectorById(el);
  if (byId) return byId;

  const byData = trySelectorByDataAttr(el);
  if (byData) return byData;

  const byClass = trySelectorByUniqueClass(el);
  if (byClass) return byClass;

  const byCombo = trySelectorByClassCombo(el);
  if (byCombo) return byCombo;

  const pathSelector = buildPathSelector(el);
  try {
    if (document.querySelectorAll(pathSelector).length === 1) return pathSelector;
  } catch {
    // invalid selector
  }
  return pathSelector;
}

/**
 * Check whether an element is interactive (clickable/focusable).
 *
 * Checks native interactive elements (button, a, input, select, textarea),
 * ARIA roles (button, link), tabindex presence, inline onclick handlers,
 * and CSS cursor:pointer — covering both semantic and styled interactions.
 */
function isInteractive(el: Element): boolean {
  const tag = el.tagName;
  if (
    tag === "BUTTON" ||
    tag === "A" ||
    tag === "INPUT" ||
    tag === "SELECT" ||
    tag === "TEXTAREA"
  )
    return true;
  if (el.getAttribute("role") === "button" || el.getAttribute("role") === "link") return true;
  if (el.getAttribute("tabindex") !== null) return true;
  if ((el as HTMLElement).onclick) return true;
  try {
    const style = window.getComputedStyle(el);
    if (style.cursor === "pointer") return true;
  } catch {
    // ignore
  }
  return false;
}

function findInteractive(el: Element | null): HTMLElement | null {
  while (el && el !== document.body) {
    if (isInteractive(el)) return el as HTMLElement;
    el = el.parentElement;
  }
  return null;
}

// --- Shared styles ---
/** Hoogte van de balk. Bepaalt hoever de pagina omlaag gaat. */
const TOOLBAR_HEIGHT_PX = 50;

/**
 * Tot hoever van de bovenkant een vastgezet element als "hangt bovenaan" telt.
 *
 * Niet nul, want een navigatiebalk staat regelmatig op `top: -1px` om een
 * randlijn weg te werken.
 */
const PINNED_TOP_THRESHOLD_PX = 4;

const TOOLBAR_BASE_STYLE = [
  "position:fixed", "top:0", "left:0", "right:0", "z-index:2147483647",
  "color:#fff", "font-family:-apple-system,BlinkMacSystemFont,sans-serif", "font-size:14px",
  "padding:12px 20px", "display:flex", "align-items:center", "justify-content:space-between",
  "box-shadow:0 2px 8px rgba(0,0,0,0.2)",
].join(";");

const BTN_STYLE_SECONDARY = [
  "background:rgba(255,255,255,0.2)", "border:none", "color:#fff",
  "padding:6px 16px", "border-radius:6px", "cursor:pointer", "font-size:13px",
].join(";");

const BTN_STYLE_PRIMARY = [
  "background:#fff", "border:none", `color:${BRAND_COLOR}`, "font-weight:600",
  "padding:6px 16px", "border-radius:6px", "cursor:pointer", "font-size:13px",
].join(";");

const ICON_SVG =
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
  '<path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>';

/**
 * Two-mode selector: Navigate mode (browse freely) → Select mode (pick element).
 *
 * Navigate mode (default): toolbar with "Start Selecting" button, normal browsing works.
 * Select mode: hover highlighting + click capture, toolbar shows "Cancel".
 */
export function startSelectorMode(sessionId: string): void {
  let hoveredEl: HTMLElement | null = null;
  let outlineBackup = "";
  /** Wat we op <body> hebben aangeraakt, om het netjes terug te zetten. */
  let originalPaddingTop = "";
  let originalPaddingBottom = "";
  /** Elementen die we opzij hebben gezet, met hun eigen oude `top`. */
  const shiftedElements: Array<{ el: HTMLElement; top: string }> = [];

  function onReady(fn: () => void): void {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  onReady(() => {
    // --- Toolbar element (reused across modes) ---
    const toolbar = document.createElement("div");
    toolbar.id = "tst-selector-toolbar";

    /**
     * Ruimte maken voor de balk zonder de pagina kapot te maken.
     *
     * Dit is twee keer eerder misgegaan en de twee fouten waren elkaars
     * spiegelbeeld.
     *
     * Eerst stond er alleen padding op de body. Dat werkt voor inhoud die
     * meeschuift, maar niet voor de navigatiebalk die vrijwel elke site
     * bovenaan vastzet: die bleef precies onder onze balk liggen, en dat is
     * juist waar mensen hun eerste conversie-element zoeken.
     *
     * Daarna werd het een `transform` op <html>. Dat schoof de vaste navigatie
     * inderdaad mee, maar een transform op de wortel maakt dat element het
     * referentiekader voor alles wat eronder `fixed` staat. Dus ook voor onze
     * eigen balk: die was niet langer aan het venster verankerd maar aan het
     * document, en schoof bij het scrollen gewoon van het scherm af. Gemeten op
     * een echte site: op scrollpositie 1200 stond de balk op -1200.
     *
     * Nu allebei apart. Padding op de body voor de gewone inhoud, en de vaste
     * of plakkende elementen van de site zelf krijgen er los vijftig pixels bij.
     * Onze balk blijft dan een gewone `fixed` balk, wat hij altijd had moeten
     * blijven.
     */
    originalPaddingTop = document.body.style.paddingTop;
    originalPaddingBottom = document.body.style.paddingBottom;
    const computed = getComputedStyle(document.body);
    document.body.style.paddingTop =
      (parseInt(computed.paddingTop) || 0) + TOOLBAR_HEIGHT_PX + "px";
    // De pagina wordt niet langer van een balk erboven, dus zonder deze extra
    // ruimte zouden de laatste vijftig pixels onbereikbaar worden.
    document.body.style.paddingBottom =
      (parseInt(computed.paddingBottom) || 0) + TOOLBAR_HEIGHT_PX + "px";
    shiftPinnedElements();

    document.body.appendChild(toolbar);

    // Start in navigate mode
    showNavigateMode();

    // --- Navigate mode ---
    function showNavigateMode(): void {
      toolbar.style.cssText = TOOLBAR_BASE_STYLE + `;background:${BRAND_COLOR};`;
      toolbar.innerHTML =
        '<div style="display:flex;align-items:center;gap:8px">' + ICON_SVG +
        "<span><strong>The Social Tap</strong> — Navigate to the right page, then start selecting</span></div>" +
        '<div style="display:flex;gap:8px">' +
        `<button id="tst-selector-start" style="${BTN_STYLE_PRIMARY}">Start Selecting</button>` +
        `<button id="tst-selector-cancel" style="${BTN_STYLE_SECONDARY}">Cancel</button>` +
        "</div>";

      document.getElementById("tst-selector-start")!.addEventListener("click", showSelectMode);
      document.getElementById("tst-selector-cancel")!.addEventListener("click", cancel);
    }

    // --- Select mode ---
    function showSelectMode(): void {
      toolbar.style.cssText = TOOLBAR_BASE_STYLE + `;background:${BRAND_COLOR};`;
      toolbar.innerHTML =
        '<div style="display:flex;align-items:center;gap:8px">' + ICON_SVG +
        "<span><strong>The Social Tap</strong> — Click the element you want to track</span></div>" +
        `<button id="tst-selector-cancel" style="${BTN_STYLE_SECONDARY}">Cancel</button>`;

      document.getElementById("tst-selector-cancel")!.addEventListener("click", cancel);

      document.addEventListener("mouseover", onMouseOver, true);
      document.addEventListener("click", onClick, true);
    }

    // --- Event handlers (select mode only) ---
    function onMouseOver(e: Event): void {
      const target = findInteractive(e.target as Element);
      if (!target || target === toolbar || toolbar.contains(target)) return;

      if (hoveredEl && hoveredEl !== target) {
        hoveredEl.style.outline = outlineBackup;
      }

      hoveredEl = target;
      outlineBackup = target.style.outline;
      target.style.outline = "3px solid " + BRAND_COLOR;
    }

    function onClick(e: Event): void {
      if (toolbar.contains(e.target as Node)) return;

      const target = findInteractive(e.target as Element);
      if (!target) return;

      e.preventDefault();
      e.stopPropagation();

      const cssSelector = generateSelector(target);
      const text = (target.innerText || target.textContent || "").trim().substring(0, MAX_ELEMENT_TEXT_LENGTH);
      const pageUrl = window.location.href;

      // Alleen loskoppelen, de opgeschoven pagina blijft staan: de groene balk
      // hieronder neemt exact de plek van de oranje over. Zouden we hier al
      // opruimen, dan schiet de navigatie van de site terug omhoog en ligt de
      // bevestiging er weer overheen. Dat was de oorspronkelijke klacht.
      detach();
      clearSession();

      // Show confirmation
      const confirmEl = document.createElement("div");
      confirmEl.style.cssText = [
        "position:fixed", "top:0", "left:0", "right:0", "z-index:2147483647",
        `height:${TOOLBAR_HEIGHT_PX}px`, "box-sizing:border-box",
        "background:#22c55e", "color:#fff",
        "font-family:-apple-system,BlinkMacSystemFont,sans-serif", "font-size:14px",
        "padding:16px 20px", "text-align:center",
        "box-shadow:0 2px 8px rgba(0,0,0,0.2)",
      ].join(";") + ";";
      confirmEl.innerHTML =
        "<strong>Element selected!</strong> You can close this tab and return to The Social Tap dashboard.";
      document.body.appendChild(confirmEl);

      // POST result to API
      fetch(state.apiUrl + "/events/selector-result", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId,
          cssSelector,
          elementText: text,
          pageUrl,
          tagName: target.tagName.toLowerCase(),
          href: (target as HTMLAnchorElement).href || null,
        }),
      }).catch(() => {
        confirmEl.style.background = "#ef4444";
        confirmEl.innerHTML = "<strong>Failed to send selection.</strong> Please try again.";
      });
    }

    /**
     * De eigen vastgezette elementen van de site vijftig pixels omlaag.
     *
     * Alles wat `fixed` of `sticky` staat en aan de bovenkant hangt telt mee.
     * Een element met `top: auto` (een balk die aan de onderkant hangt) heeft
     * hier niets te zoeken en wordt overgeslagen, want `parseFloat` geeft daar
     * NaN op.
     *
     * Dit gebeurt één keer, bij de start. Wat de site later zelf toevoegt
     * schuift niet mee, en dat is een bewuste grens: de vaste meubels van een
     * pagina staan er bij het laden al.
     */
    function shiftPinnedElements(): void {
      const all = document.body.querySelectorAll<HTMLElement>("*");
      for (const el of all) {
        if (el === toolbar) continue;
        const style = getComputedStyle(el);
        if (style.position !== "fixed" && style.position !== "sticky") continue;
        const top = parseFloat(style.top);
        if (Number.isNaN(top) || top > PINNED_TOP_THRESHOLD_PX) continue;

        shiftedElements.push({ el, top: el.style.top });
        el.style.top = top + TOOLBAR_HEIGHT_PX + "px";
      }
    }

    // --- Cleanup helpers ---
    /** Stoppen met meekijken en onze balk weghalen. De pagina blijft opgeschoven. */
    function detach(): void {
      if (hoveredEl) {
        hoveredEl.style.outline = outlineBackup;
        hoveredEl = null;
      }
      document.removeEventListener("mouseover", onMouseOver, true);
      document.removeEventListener("click", onClick, true);
      if (toolbar.parentNode) toolbar.parentNode.removeChild(toolbar);
    }

    /** Loskoppelen én de pagina teruggeven zoals we hem aantroffen. */
    function cleanup(): void {
      detach();
      document.body.style.paddingTop = originalPaddingTop;
      document.body.style.paddingBottom = originalPaddingBottom;
      for (const shifted of shiftedElements) {
        shifted.el.style.top = shifted.top;
      }
      shiftedElements.length = 0;
    }

    function clearSession(): void {
      try {
        sessionStorage.removeItem(STORAGE_KEY_SELECTOR);
      } catch {
        // sessionStorage not available
      }
    }

    function cancel(): void {
      cleanup();
      clearSession();
    }
  });
}
